import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Tag } from 'primereact/tag';
import { Chip } from 'primereact/chip';
import { Card } from 'primereact/card';
import { ProgressBar } from 'primereact/progressbar';
import { Badge } from 'primereact/badge';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { TabView, TabPanel } from 'primereact/tabview';
import { FilterMatchMode } from 'primereact/api';
import axiosInstance from '../config/axios.config';

// Helper functions
const getStatusSeverity = (status) => {
    const severityMap = {
        'new': 'secondary',
        'contacted': 'info',
        'interested': 'success',
        'qualified': 'success',
        'ready_to_apply': 'warning',
        'converted': 'success',
        'not_interested': 'danger',
        'disqualified': 'danger',
        'lost': 'secondary'
    };
    return severityMap[status] || 'info';
};

const getScoreSeverity = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    if (score >= 40) return 'info';
    return 'danger';
};

const LoanProspects = () => {
    const [prospects, setProspects] = useState([]);
    const [selectedProspect, setSelectedProspect] = useState(null);
    const [prospectDialog, setProspectDialog] = useState(false);
    const [prospectDetailDialog, setProspectDetailDialog] = useState(false);
    const [newProspectDialog, setNewProspectDialog] = useState(false);
    const [communicationDialog, setCommunicationDialog] = useState(false);
    const [conversionDialog, setConversionDialog] = useState(false);
    const [loading, setLoading] = useState(true);
    const [globalFilter, setGlobalFilter] = useState('');
    const [statistics, setStatistics] = useState(null);
    const [loanProducts, setLoanProducts] = useState([]);
    const [newProspect, setNewProspect] = useState({});
    const [communication, setCommunication] = useState({});
    
    const toast = useRef(null);
    const dt = useRef(null);

    const [filters, setFilters] = useState({
        'global': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'full_name': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'prospect_status': { value: null, matchMode: FilterMatchMode.EQUALS },
        'lead_source': { value: null, matchMode: FilterMatchMode.EQUALS },
        'qualification_status': { value: null, matchMode: FilterMatchMode.EQUALS },
    });

    const prospectStatusOptions = [
        { label: 'New', value: 'new' },
        { label: 'Contacted', value: 'contacted' },
        { label: 'Interested', value: 'interested' },
        { label: 'Qualified', value: 'qualified' },
        { label: 'Nurturing', value: 'nurturing' },
        { label: 'Ready to Apply', value: 'ready_to_apply' },
        { label: 'Converted', value: 'converted' },
        { label: 'Not Interested', value: 'not_interested' },
        { label: 'Disqualified', value: 'disqualified' },
        { label: 'Lost', value: 'lost' },
    ];

    const leadSourceOptions = [
        { label: 'Website', value: 'website' },
        { label: 'Social Media', value: 'social_media' },
        { label: 'Referral', value: 'referral' },
        { label: 'Advertisement', value: 'advertisement' },
        { label: 'Cold Call', value: 'cold_call' },
        { label: 'Walk-in', value: 'walk_in' },
        { label: 'Partner', value: 'partner' },
        { label: 'Existing Customer', value: 'existing_customer' },
    ];

    useEffect(() => {
        loadProspects();
        loadStatistics();
        loadLoanProducts();
    }, []);

    const loadProspects = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/prospects');
            if (response.data.success) {
                setProspects(response.data.data || []);
            }
        } catch (error) {
            console.error('Error loading prospects:', error);
            setProspects([]);
            if (error.response?.status === 401) {
                toast.current.show({ 
                    severity: 'warn', 
                    summary: 'Authentication Required', 
                    detail: 'Please log in to view prospects',
                    life: 3000 
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const loadStatistics = async () => {
        try {
            const response = await axiosInstance.get('/prospects/statistics');
            if (response.data.success) {
                setStatistics(response.data.data);
            }
        } catch (error) {
            console.error('Error loading statistics:', error);
            setStatistics({
                total_prospects: 0,
                qualified_prospects: 0,
                requiring_follow_up: 0,
                hot_prospects: 0,
                conversion_rate: 0
            });
        }
    };

    const loadLoanProducts = async () => {
        try {
            const response = await axiosInstance.get('/loan-products/active');
            if (response.data.success) {
                const productOptions = response.data.data.map(product => ({
                    label: `${product.product_name} (${product.base_rate}%)`,
                    value: product.id
                }));
                setLoanProducts(productOptions);
            }
        } catch (error) {
            console.error('Error loading loan products:', error);
        }
    };

    const openNewProspect = () => {
        setNewProspect({
            first_name: '',
            last_name: '',
            phone: '',
            email: '',
            monthly_income: null,
            desired_loan_amount: 100000,
            desired_tenure_months: 24,
            employment_type: 'salaried',
            loan_purpose: 'personal_needs',
            lead_source: 'website',
            interested_loan_types: ['personal']
        });
        setNewProspectDialog(true);
    };

    const createNewProspect = async () => {
        try {
            // Basic validation
            if (!newProspect.first_name || !newProspect.phone) {
                toast.current.show({ 
                    severity: 'error', 
                    summary: 'Validation Error', 
                    detail: 'Please provide at least first name and phone number',
                    life: 3000 
                });
                return;
            }

            const response = await axiosInstance.post('/prospects', newProspect);
            
            if (response.data.success) {
                toast.current.show({ 
                    severity: 'success', 
                    summary: 'Success', 
                    detail: 'Prospect created successfully', 
                    life: 3000 
                });
                setNewProspectDialog(false);
                loadProspects();
                loadStatistics();
            }
        } catch (error) {
            console.error('Error creating prospect:', error);
            const errorMessage = error.response?.data?.message || 'Failed to create prospect';
            toast.current.show({ 
                severity: 'error', 
                summary: 'Error', 
                detail: errorMessage, 
                life: 5000 
            });
        }
    };

    const convertToApplication = async (prospect, productId) => {
        try {
            const response = await axiosInstance.post(`/prospects/${prospect.id}/convert-to-application`, {
                loan_product_id: productId
            });
            
            if (response.data.success) {
                toast.current.show({ 
                    severity: 'success', 
                    summary: 'Success', 
                    detail: 'Prospect converted to application successfully', 
                    life: 3000 
                });
                loadProspects();
                loadStatistics();
                setConversionDialog(false);
            }
        } catch (error) {
            console.error('Error converting prospect:', error);
            const errorMessage = error.response?.data?.message || 'Failed to convert prospect';
            toast.current.show({ 
                severity: 'error', 
                summary: 'Error', 
                detail: errorMessage, 
                life: 5000 
            });
        }
    };

    const statusBodyTemplate = (rowData) => {
        return <Tag value={rowData.prospect_status} severity={getStatusSeverity(rowData.prospect_status)} />;
    };

    const leadScoreBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center gap-2">
                <Tag value={rowData.lead_score || 0} severity={getScoreSeverity(rowData.lead_score || 0)} />
                {rowData.is_hot_prospect && <Badge value="🔥" severity="danger" />}
            </div>
        );
    };

    const amountBodyTemplate = (rowData) => {
        return rowData.desired_loan_amount ? 
            `₹${rowData.desired_loan_amount.toLocaleString()}` : '-';
    };

    const followUpBodyTemplate = (rowData) => {
        if (rowData.needs_follow_up) {
            return <Badge value="Due" severity="danger" />;
        }
        return rowData.next_follow_up_at ? 
            new Date(rowData.next_follow_up_at).toLocaleDateString() : '-';
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                <Button 
                    icon="pi pi-eye" 
                    className="p-button-rounded p-button-info p-button-text" 
                    onClick={() => viewProspectDetails(rowData)}
                    tooltip="View Details"
                />
                {rowData.prospect_status === 'ready_to_apply' && (
                    <Button 
                        icon="pi pi-arrow-right" 
                        className="p-button-rounded p-button-success p-button-text" 
                        onClick={() => {
                            setSelectedProspect(rowData);
                            setConversionDialog(true);
                        }}
                        tooltip="Convert to Application"
                    />
                )}
                <Button 
                    icon="pi pi-phone" 
                    className="p-button-rounded p-button-warning p-button-text" 
                    onClick={() => {
                        setSelectedProspect(rowData);
                        setCommunicationDialog(true);
                    }}
                    tooltip="Log Communication"
                />
            </div>
        );
    };

    const viewProspectDetails = (prospect) => {
        setSelectedProspect(prospect);
        setProspectDetailDialog(true);
    };

    const leftToolbarTemplate = () => {
        return (
            <div className="flex align-items-center gap-2">
                <Button label="New Prospect" icon="pi pi-plus" className="p-button-success" onClick={openNewProspect} />
                <Button label="Refresh" icon="pi pi-refresh" className="p-button-help" onClick={loadProspects} />
            </div>
        );
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between">
            <h5 className="m-0">Loan Prospects</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder="Search..." />
            </span>
        </div>
    );

    return (
        <div className="grid">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    
                    {/* Statistics Cards */}
                    {statistics && (
                        <div className="grid mb-4">
                            <div className="col-12 lg:col-3">
                                <Card className="bg-blue-100">
                                    <div className="flex justify-content-between mb-3">
                                        <div>
                                            <span className="block text-500 font-medium mb-3">Total Prospects</span>
                                            <div className="text-900 font-medium text-xl">{statistics.total_prospects}</div>
                                        </div>
                                        <div className="flex align-items-center justify-content-center bg-blue-100 border-round" 
                                             style={{width: '2.5rem', height: '2.5rem'}}>
                                            <i className="pi pi-users text-blue-500 text-xl"></i>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                            <div className="col-12 lg:col-3">
                                <Card className="bg-green-100">
                                    <div className="flex justify-content-between mb-3">
                                        <div>
                                            <span className="block text-500 font-medium mb-3">Qualified</span>
                                            <div className="text-900 font-medium text-xl">{statistics.qualified_prospects}</div>
                                        </div>
                                        <div className="flex align-items-center justify-content-center bg-green-100 border-round" 
                                             style={{width: '2.5rem', height: '2.5rem'}}>
                                            <i className="pi pi-check-circle text-green-500 text-xl"></i>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                            <div className="col-12 lg:col-3">
                                <Card className="bg-orange-100">
                                    <div className="flex justify-content-between mb-3">
                                        <div>
                                            <span className="block text-500 font-medium mb-3">Follow-up Due</span>
                                            <div className="text-900 font-medium text-xl">{statistics.requiring_follow_up}</div>
                                        </div>
                                        <div className="flex align-items-center justify-content-center bg-orange-100 border-round" 
                                             style={{width: '2.5rem', height: '2.5rem'}}>
                                            <i className="pi pi-clock text-orange-500 text-xl"></i>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                            <div className="col-12 lg:col-3">
                                <Card className="bg-red-100">
                                    <div className="flex justify-content-between mb-3">
                                        <div>
                                            <span className="block text-500 font-medium mb-3">Hot Prospects 🔥</span>
                                            <div className="text-900 font-medium text-xl">{statistics.hot_prospects}</div>
                                        </div>
                                        <div className="flex align-items-center justify-content-center bg-red-100 border-round" 
                                             style={{width: '2.5rem', height: '2.5rem'}}>
                                            <i className="pi pi-star text-red-500 text-xl"></i>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                        </div>
                    )}

                    <Toolbar className="mb-4" left={leftToolbarTemplate}></Toolbar>

                    <DataTable
                        ref={dt}
                        value={prospects}
                        dataKey="id"
                        paginator
                        rows={15}
                        rowsPerPageOptions={[10, 15, 25]}
                        className="p-datatable-gridlines"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} prospects"
                        globalFilter={globalFilter}
                        filters={filters}
                        filterDisplay="menu"
                        loading={loading}
                        responsiveLayout="scroll"
                        header={header}
                    >
                        <Column field="full_name" header="Name" sortable style={{ minWidth: '12rem' }} />
                        <Column field="phone" header="Phone" sortable style={{ minWidth: '10rem' }} />
                        <Column field="prospect_status" header="Status" body={statusBodyTemplate} sortable style={{ minWidth: '10rem' }} />
                        <Column field="lead_source" header="Source" sortable style={{ minWidth: '8rem' }} />
                        <Column field="lead_score" header="Score" body={leadScoreBodyTemplate} sortable style={{ minWidth: '8rem' }} />
                        <Column field="desired_loan_amount" header="Desired Amount" body={amountBodyTemplate} style={{ minWidth: '12rem' }} />
                        <Column header="Follow-up" body={followUpBodyTemplate} style={{ minWidth: '10rem' }} />
                        <Column body={actionBodyTemplate} header="Actions" style={{ minWidth: '12rem' }}></Column>
                    </DataTable>

                    {/* New Prospect Dialog */}
                    <Dialog 
                        visible={newProspectDialog} 
                        style={{ width: '800px' }} 
                        header="Create New Prospect" 
                        modal 
                        className="p-fluid"
                        onHide={() => setNewProspectDialog(false)}
                        footer={
                            <div>
                                <Button label="Cancel" icon="pi pi-times" className="p-button-text" 
                                    onClick={() => setNewProspectDialog(false)} />
                                <Button label="Create Prospect" icon="pi pi-check" className="p-button-text" 
                                    onClick={createNewProspect} />
                            </div>
                        }
                    >
                        <TabView>
                            <TabPanel header="Basic Details">
                                <div className="grid">
                                    <div className="col-12 md:col-6">
                                        <div className="field">
                                            <label htmlFor="first_name">First Name *</label>
                                            <InputText 
                                                id="first_name" 
                                                value={newProspect.first_name || ''} 
                                                onChange={(e) => setNewProspect({...newProspect, first_name: e.target.value})} 
                                                placeholder="Enter first name"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="field">
                                            <label htmlFor="last_name">Last Name</label>
                                            <InputText 
                                                id="last_name" 
                                                value={newProspect.last_name || ''} 
                                                onChange={(e) => setNewProspect({...newProspect, last_name: e.target.value})} 
                                                placeholder="Enter last name"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="field">
                                            <label htmlFor="phone">Phone Number *</label>
                                            <InputText 
                                                id="phone" 
                                                value={newProspect.phone || ''} 
                                                onChange={(e) => setNewProspect({...newProspect, phone: e.target.value})} 
                                                placeholder="Enter phone number"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="field">
                                            <label htmlFor="email">Email</label>
                                            <InputText 
                                                id="email" 
                                                value={newProspect.email || ''} 
                                                onChange={(e) => setNewProspect({...newProspect, email: e.target.value})} 
                                                placeholder="Enter email"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </TabPanel>
                            <TabPanel header="Loan Preferences">
                                <div className="grid">
                                    <div className="col-12 md:col-6">
                                        <div className="field">
                                            <label htmlFor="desired_amount">Desired Amount (₹)</label>
                                            <InputNumber 
                                                id="desired_amount" 
                                                value={newProspect.desired_loan_amount} 
                                                onValueChange={(e) => setNewProspect({...newProspect, desired_loan_amount: e.value})} 
                                                mode="currency" 
                                                currency="INR"
                                                locale="en-IN"
                                                min={1000}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="field">
                                            <label htmlFor="desired_tenure">Desired Tenure (Months)</label>
                                            <InputNumber 
                                                id="desired_tenure" 
                                                value={newProspect.desired_tenure_months} 
                                                onValueChange={(e) => setNewProspect({...newProspect, desired_tenure_months: e.value})} 
                                                min={6}
                                                max={360}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="field">
                                            <label htmlFor="monthly_income">Monthly Income (₹)</label>
                                            <InputNumber 
                                                id="monthly_income" 
                                                value={newProspect.monthly_income} 
                                                onValueChange={(e) => setNewProspect({...newProspect, monthly_income: e.value})} 
                                                mode="currency" 
                                                currency="INR"
                                                locale="en-IN"
                                                min={0}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="field">
                                            <label htmlFor="employment_type">Employment Type</label>
                                            <Dropdown 
                                                id="employment_type"
                                                value={newProspect.employment_type} 
                                                options={[
                                                    { label: 'Salaried', value: 'salaried' },
                                                    { label: 'Self Employed', value: 'self_employed' },
                                                    { label: 'Business', value: 'business' },
                                                    { label: 'Professional', value: 'professional' },
                                                ]} 
                                                onChange={(e) => setNewProspect({...newProspect, employment_type: e.value})} 
                                                placeholder="Select Employment Type"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </TabPanel>
                        </TabView>
                    </Dialog>

                    {/* Prospect Detail Dialog */}
                    <Dialog 
                        visible={prospectDetailDialog} 
                        style={{ width: '1000px' }} 
                        header={selectedProspect ? `Prospect: ${selectedProspect.full_name}` : ''} 
                        modal 
                        onHide={() => setProspectDetailDialog(false)}
                        maximizable
                    >
                        {selectedProspect && <ProspectDetails prospect={selectedProspect} />}
                    </Dialog>

                    {/* Conversion Dialog */}
                    <Dialog 
                        visible={conversionDialog} 
                        style={{ width: '600px' }} 
                        header="Convert to Loan Application" 
                        modal 
                        onHide={() => setConversionDialog(false)}
                    >
                        {selectedProspect && (
                            <div className="grid">
                                <div className="col-12">
                                    <h6>Converting: {selectedProspect.full_name}</h6>
                                    <p>Desired Amount: ₹{selectedProspect.desired_loan_amount?.toLocaleString()}</p>
                                </div>
                                <div className="col-12">
                                    <div className="field">
                                        <label>Select Loan Product *</label>
                                        <Dropdown 
                                            value="" 
                                            options={loanProducts} 
                                            onChange={(e) => convertToApplication(selectedProspect, e.value)} 
                                            placeholder="Select Product to Convert"
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

// Supporting Components
const ProspectDetails = ({ prospect }) => (
    <TabView>
        <TabPanel header="Overview">
            <div className="grid">
                <div className="col-12 md:col-6">
                    <Card title="Contact Information">
                        <div className="field">
                            <label>Name:</label>
                            <div className="font-bold">{prospect.full_name}</div>
                        </div>
                        <div className="field">
                            <label>Phone:</label>
                            <div className="font-bold">{prospect.phone}</div>
                        </div>
                        <div className="field">
                            <label>Email:</label>
                            <div className="font-bold">{prospect.email || 'Not provided'}</div>
                        </div>
                        <div className="field">
                            <label>Age:</label>
                            <div className="font-bold">{prospect.age || 'Unknown'} years</div>
                        </div>
                    </Card>
                </div>
                <div className="col-12 md:col-6">
                    <Card title="Loan Requirements">
                        <div className="field">
                            <label>Desired Amount:</label>
                            <div className="font-bold text-2xl text-blue-500">
                                ₹{prospect.desired_loan_amount?.toLocaleString() || 'Not specified'}
                            </div>
                        </div>
                        <div className="field">
                            <label>Desired Tenure:</label>
                            <div className="font-bold">{prospect.desired_tenure_months || 'Not specified'} months</div>
                        </div>
                        <div className="field">
                            <label>Estimated EMI:</label>
                            <div className="font-bold text-green-500">₹{prospect.estimated_emi?.toLocaleString() || 'N/A'}</div>
                        </div>
                    </Card>
                </div>
                <div className="col-12">
                    <Card title="Prospect Status">
                        <div className="grid">
                            <div className="col-3">
                                <div className="text-center">
                                    <Tag value={prospect.prospect_status} severity={getStatusSeverity(prospect.prospect_status)} />
                                    <div className="text-500 mt-1">Current Status</div>
                                </div>
                            </div>
                            <div className="col-3">
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-blue-500">{prospect.lead_score || 0}</div>
                                    <div className="text-500">Lead Score</div>
                                </div>
                            </div>
                            <div className="col-3">
                                <div className="text-center">
                                    <Chip label={prospect.priority} />
                                    <div className="text-500 mt-1">Priority</div>
                                </div>
                            </div>
                            <div className="col-3">
                                <div className="text-center">
                                    <div className="text-lg font-semibold">{prospect.contact_attempts || 0}</div>
                                    <div className="text-500">Contact Attempts</div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </TabPanel>
    </TabView>
);

export default LoanProspects;